import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Mic, Plus, Trash2, Edit3, X, Save, Feather } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import MultilingualEditor from "@/components/ui/MultilingualEditor";
import MultilingualText from "@/components/ui/MultilingualText";
import { packMl, hasMlContent } from "@/lib/ml";
import { toast } from "sonner";

const CHAPTERS = [
  ["childhood", "Childhood"],
  ["education", "Education"],
  ["work", "Work & Calling"],
  ["love", "Love"],
  ["family", "Family"],
  ["home", "Home & Places"],
  ["travel", "Journeys"],
  ["loss", "Loss & Remembrance"],
  ["wisdom", "Wisdom"],
  ["other", "Other"],
];
const CHAPTER_LABELS = Object.fromEntries(CHAPTERS);

const ease = [0.22, 1, 0.36, 1];

/**
 * /app/journal — My Life. A private timeline of dated life chapters.
 * Entries are multilingual; interviews arrive here via /app/interview.
 */
export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // composer state
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [date, setDate] = useState("");
  const [chapter, setChapter] = useState("childhood");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/v1/journal");
      setEntries(data.entries || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  function resetComposer() {
    setTitle("");
    setBody("");
    setDate("");
    setChapter("childhood");
    setEditingId(null);
    setComposing(false);
  }

  function startEdit(entry) {
    setEditingId(entry.id);
    setTitle(entry.title || "");
    setBody(entry.body || "");
    setDate(entry.entry_date || "");
    setChapter(entry.chapter || "other");
    setComposing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function save(e) {
    e.preventDefault();
    if (!hasMlContent(title) || !hasMlContent(body)) {
      toast.error("A title and a few words are needed.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: packMl(title),
        body: packMl(body),
        entry_date: date.trim() || undefined,
        chapter,
      };
      if (editingId) {
        await api.patch(`/v1/journal/${editingId}`, payload);
        toast.success("Chapter revised.");
      } else {
        await api.post("/v1/journal", { ...payload, source: "written" });
        toast.success("A new chapter is written.");
      }
      resetComposer();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    if (!confirm("Tear this page out of your journal?")) return;
    try {
      await api.delete(`/v1/journal/${id}`);
      toast.success("Page removed.");
      setEntries((es) => es.filter((e) => e.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  // Group by year for the timeline
  const byYear = useMemo(() => {
    const groups = new Map();
    for (const e of entries) {
      const y = (e.entry_date || "").match(/(\d{4})/)?.[1] || "Undated";
      if (!groups.has(y)) groups.set(y, []);
      groups.get(y).push(e);
    }
    return [...groups.entries()];
  }, [entries]);

  return (
    <section className="mx-auto max-w-3xl px-6 md:px-10 pt-8 pb-24" data-testid="journal-page">
      <header className="mb-10 text-center">
        <div className="overline mb-2">§ My Life</div>
        <h1 className="heritage-title text-4xl md:text-6xl leading-tight">The book of you</h1>
        <p className="mt-3 font-italic-serif italic text-[color:var(--ink-soft)] max-w-lg mx-auto">
          One day your grandchildren will read these pages. Write a chapter — or speak it.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <button
            data-testid="journal-new"
            onClick={() => (composing ? resetComposer() : setComposing(true))}
            className="btn-wood !py-2.5"
          >
            {composing ? <X size={14} /> : <Plus size={14} />} {composing ? "Close" : "Write a chapter"}
          </button>
          <Link to="/app/interview" data-testid="journal-interview-link" className="btn-ghost !py-2.5">
            <Mic size={14} /> Answer with your voice
          </Link>
        </div>
      </header>

      {/* Composer */}
      <AnimatePresence>
        {composing && (
          <motion.form
            onSubmit={save}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4, ease }}
            className="border border-[color:var(--sepia)]/30 bg-[color:var(--parchment)] p-6 md:p-8 mb-12 shadow-[0_16px_44px_-24px_rgba(43,28,15,0.4)]"
            data-testid="journal-composer"
          >
            <div className="overline mb-4">{editingId ? "Revise this chapter" : "A new chapter"}</div>
            <div className="grid md:grid-cols-2 gap-4 mb-5">
              <div>
                <span className="overline block mb-1">When</span>
                <input
                  data-testid="journal-date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  placeholder="1978 · 1978-06 · 1978-06-21"
                  className="w-full bg-transparent border-b border-[color:var(--sepia)]/50 py-2 focus:outline-none focus:border-[color:var(--rust)]"
                />
              </div>
              <div>
                <span className="overline block mb-1">Chapter of life</span>
                <select
                  data-testid="journal-chapter"
                  value={chapter}
                  onChange={(e) => setChapter(e.target.value)}
                  className="w-full bg-transparent border-b border-[color:var(--sepia)]/50 py-2 focus:outline-none focus:border-[color:var(--rust)]"
                >
                  {CHAPTERS.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
            </div>
            <MultilingualEditor
              as="input"
              testid="journal-title"
              label="Title"
              value={title}
              onChange={setTitle}
              maxLength={200}
              placeholder="The monsoon of seventy-eight…"
            />
            <div className="mt-5">
              <MultilingualEditor
                as="textarea"
                rows={5}
                testid="journal-body"
                label="Your words"
                value={body}
                onChange={setBody}
                maxLength={4000}
                placeholder="Write it as you'd tell it at the kitchen table…"
              />
            </div>
            <button
              type="submit"
              data-testid="journal-save"
              disabled={saving || !hasMlContent(title) || !hasMlContent(body)}
              className="btn-wood mt-6 disabled:opacity-50"
            >
              <Save size={14} /> {saving ? "Saving…" : editingId ? "Save changes" : "Press into the book"}
            </button>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Timeline */}
      {loading ? (
        <div className="py-16 text-center text-[color:var(--ink-soft)]">Opening your book…</div>
      ) : entries.length === 0 && !composing ? (
        <div className="border border-dashed border-[color:var(--ink-line-strong)] p-12 text-center">
          <BookOpen size={26} className="mx-auto text-[color:var(--sepia)]" />
          <div className="mt-3 font-italic-serif italic text-xl text-[color:var(--ink-soft)]">
            "Every life is a volume worth keeping."
          </div>
          <p className="mt-2 text-sm text-[color:var(--ink-soft)]">
            Begin with one small memory — a smell, a street, a song.
          </p>
        </div>
      ) : (
        <div className="space-y-12" data-testid="journal-timeline">
          {byYear.map(([year, list]) => (
            <div key={year}>
              <div className="flex items-center gap-4 mb-5">
                <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
                <div className="font-serif-display text-2xl text-[color:var(--sepia)]">{year}</div>
                <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
              </div>
              <ol className="space-y-8">
                {list.map((e) => (
                  <motion.li
                    key={e.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease }}
                    className="border-l-2 border-[color:var(--sepia)]/50 pl-5 group"
                    data-testid={`journal-entry-${e.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="overline text-[color:var(--sepia)]">
                          {CHAPTER_LABELS[e.chapter] || "Other"}
                          {e.entry_date && <span> · {e.entry_date}</span>}
                          {e.source === "interview" && (
                            <span className="inline-flex items-center gap-1 ml-1"><Mic size={10} /> spoken</span>
                          )}
                        </div>
                        <MultilingualText
                          value={e.title}
                          className="mt-1 font-serif-display text-2xl leading-snug"
                          testid={`journal-title-text-${e.id}`}
                        />
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity shrink-0">
                        <button
                          data-testid={`journal-edit-${e.id}`}
                          onClick={() => startEdit(e)}
                          className="p-2 border border-[color:var(--ink-line-strong)] hover:bg-[color:var(--bone-2)]"
                          title="Edit"
                        >
                          <Edit3 size={13} />
                        </button>
                        <button
                          data-testid={`journal-delete-${e.id}`}
                          onClick={() => remove(e.id)}
                          className="p-2 border border-[color:var(--ink-line-strong)] hover:bg-[color:var(--rust)] hover:text-[color:var(--bone)]"
                          title="Delete"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    <MultilingualText
                      value={e.body}
                      className="mt-2 text-[16px] leading-[1.65] text-[color:var(--ink-soft)] whitespace-pre-wrap"
                      testid={`journal-body-text-${e.id}`}
                    />
                  </motion.li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}

      <div className="mt-16 text-center">
        <Feather size={16} className="mx-auto text-[color:var(--sepia)]/60" />
        <p className="mt-2 numeric-index text-[color:var(--ink-soft)]">
          {entries.length} {entries.length === 1 ? "page" : "pages"} · private to you
        </p>
      </div>
    </section>
  );
}
